import { useSyncExternalStore } from 'react';
import { developmentApplicationsApi } from '../config/api';

/**
 * How many saved DAs this user has not seen on the Saved page yet — the sidebar's Saved badge.
 *
 * A module store rather than layout state, because the things that change it live in pages the
 * layout knows nothing about: a star on the dashboard, the detail page or Search refreshes it, and
 * opening Saved zeroes it. Fetched on demand only — on the layout's mount and after those actions
 * — never polled: a colleague's save shows up on the next one of them.
 */
let count = 0;
const listeners = new Set<() => void>();
/** Bumped by every refresh and clear, so a slow answer cannot overwrite a newer state. */
let generation = 0;

function set(next: number) {
    if (next === count) return;
    count = next;
    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

/**
 * Re-asks the server. A failure leaves the badge as it was: it is a passive count feeding a
 * decorative badge, so there is nothing for an error to be actionable about.
 */
export async function refreshNewSavedCount(): Promise<void> {
    const mine = ++generation;
    try {
        const { count: latest } = await developmentApplicationsApi.getSavedNewCount();
        if (mine === generation) set(latest);
    } catch {
        /* keep what we had */
    }
}

/** The Saved page has been seen — drop the badge now, without waiting for a refetch. */
export function clearNewSavedCount(): void {
    generation++;
    set(0);
}

export function useNewSavedCount(): number {
    return useSyncExternalStore(subscribe, () => count, () => 0);
}
